import React from 'react';

/**
 * Generates a favicon URL from a website URL
 * @param websiteUrl - The website URL to get favicon for
 * @returns The favicon URL or null if invalid
 */
export const getFaviconUrl = (websiteUrl: string | undefined): string | null => {
  if (!websiteUrl) return null;
  
  try {
    const url = new URL(websiteUrl);
    // Use Google's favicon service for reliable favicon fetching
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=128`;
  } catch {
    return null;
  }
};

/**
 * React component for displaying a favicon
 * @param domain - The domain to get favicon for
 * @param websiteUrl - The website URL to get favicon for (alternative to domain)
 * @param size - Size of the favicon (default: 12)
 * @param retinaMultiplier - Multiplier for retina displays (default: 1)
 * @param alt - Alt text for the image
 * @param className - CSS class name
 * @param style - Inline styles
 * @param onError - Error handler function
 */
interface FaviconProps {
  domain?: string;
  websiteUrl?: string;
  size?: number;
  retinaMultiplier?: number;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
}

export const Favicon: React.FC<FaviconProps> = ({ 
  domain,
  websiteUrl, 
  size = 12, 
  retinaMultiplier = 3,
  alt = "Website favicon", 
  className,
  style,
  onError
}) => {
  // Use domain if provided, otherwise extract from websiteUrl
  let faviconDomain = domain;
  
  if (!faviconDomain && websiteUrl) {
    try {
      faviconDomain = new URL(websiteUrl).hostname;
    } catch {
      // Invalid URL, skip favicon
      return null;
    }
  }
  
  if (!faviconDomain) return null;
  
  // Try original favicon first (no white background), fallback to Google's service

  const googleFaviconUrl = `https://www.google.com/s2/favicons?domain=${faviconDomain}&sz=128`;
  
  return (
    <img
      src={googleFaviconUrl}
      alt={alt}
      width={size}
      height={size}
      className={className}
      style={style}
      onError={(e) => {
        const img = e.target as HTMLImageElement;
        // If original favicon fails, try Google's service
        if (img.src === googleFaviconUrl) {
          img.src = googleFaviconUrl;
        } else {
          // Both failed, hide the image or call onError
          if (onError) {
            onError();
          } else {
            img.style.display = 'none';
          }
        }
      }}
    />
  );
};